import type Row from "@/types/Mapping/Row";
import type Seat from "@/types/Mapping/Seat";
import type { SeatStatus, SeatType } from "@/types/Mapping/Seat";

/** How a seat looks: its status, or "selected" once the buyer picked it. */
export type SeatLook = SeatStatus | "selected";

export const SEAT_COLORS = {
  available: "#05ae01",
  reserved: "#2681f2",
  occupied: "#e20f24",
  selected: "#f7b500",
  invalid: "#bdbdbd",
  blocked: "#bdbdbd",
} as const satisfies Record<SeatLook, string>;

/** Shown inside the seat at every zoom level. */
export const SEAT_TYPE_ICONS = {
  wheelchair: "mdi-wheelchair",
  plus_size: "mdi-arrow-expand-horizontal",
  reduced_mobility: "mdi-human-cane",
} as const satisfies Record<SpecialSeatType, string>;

export const SEAT_TYPE_NAMES = {
  wheelchair: "Espaço para cadeira de rodas",
  plus_size: "Pessoa obesa (assento maior)",
  reduced_mobility: "Mobilidade reduzida",
} as const satisfies Record<SpecialSeatType, string>;

/** A row of the seat grid, one cell per column. */
export interface SeatGridRow {
  row: Row;
  /** `null` where the column has no seat: an aisle, a gap or the end of a shorter row. */
  cells: (Seat | null)[];
}

/** A Section's seats as they sit in the venue, the stage at the top. */
export interface SeatGrid {
  /** The highest Seat Position in the Section. */
  columns: number;
  /** Ordered by Row order, the closest to the stage first. */
  rows: SeatGridRow[];
}

function seatsOf(row: Row): Seat[] {
  return row.seats ?? [];
}

export function seatGrid(rows: Row[]): SeatGrid {
  const columns = Math.max(0, ...rows.flatMap(row => seatsOf(row).map(seat => seat.position)));

  return {
    columns,
    rows: [ ...rows ]
      .sort((a, b) => a.order - b.order)
      .map((row) => {
        const byPosition = new Map(seatsOf(row).map(seat => [seat.position, seat]));

        return { row, cells: Array.from({ length: columns }, (_, i) => byPosition.get(i + 1) ?? null) };
      }),
  };
}

/** Seat Types other than standard, in the order the legend lists them. */
const SPECIAL_TYPES = ["wheelchair", "plus_size", "reduced_mobility"] as const satisfies SeatType[];

export type SpecialSeatType = typeof SPECIAL_TYPES[number];

/** Seat Types other than standard that the Section has, whatever the seats' status. */
export function seatTypesIn(rows: Row[]): SpecialSeatType[] {
  const found = new Set<SeatType>(rows.flatMap(row => seatsOf(row).map(seat => seat.type)));

  return SPECIAL_TYPES.filter(type => found.has(type));
}

/** "Linha A · Assento 12", the seat's full label being its row's symbol followed by its own label. */
export function seatLabel(seat: Seat): string {
  const row = seat.full_label.slice(0, seat.full_label.length - seat.label.length);

  return `Linha ${row} · Assento ${seat.label}`;
}
